import { Injectable, inject } from '@angular/core';
import { MsalService } from '@azure/msal-angular';
import { AccountInfo } from '@azure/msal-browser';
import { TokenStorageService } from './token-storage.service';

@Injectable({ providedIn: 'root' })
export class MsalSessionService {
  private readonly msalService = inject(MsalService);
  private readonly tokenStorage = inject(TokenStorageService);

  hasAccount(): boolean {
    return this.msalService.instance.getAllAccounts().length > 0;
  }

  /**
   * Makes a completed Azure sign-in the one active identity — the mirror of what
   * OtpSessionService does for an OTP login. Without this, an Azure login next to an existing OTP
   * session leaves the UI showing the Azure user while every API call still carries the OTP
   * bearer, because msalGateInterceptor defers to any present OTP session.
   */
  startAzureSession(account: AccountInfo): void {
    this.tokenStorage.clear();
    this.msalService.instance.setActiveAccount(account);
  }

  /**
   * Re-activates an Azure account that is already in the cache. Two tabs establishing their
   * sessions at the same time can still leave an Azure account and an OTP token side by side,
   * so this re-asserts on every boot that only one of them survives.
   */
  restoreActiveAccount(): void {
    const [account] = this.msalService.instance.getAllAccounts();
    if (account) {
      this.startAzureSession(account);
    }
  }

  /**
   * Drops the locally cached Azure identity without the redirect that a full MSAL logout performs,
   * so it can run inside a flow that must stay on the page. Callers that end an Azure session on
   * purpose want `logoutRedirect` instead — this only removes the browser-side cache, the session
   * at the identity provider survives.
   *
   * Runs unconditionally rather than only when an account is present: Microsoft token or state
   * material can outlive the account entry it belongs to, and clearing an already-empty cache is
   * cheap. The account check afterwards verifies the outcome rather than gating the call — MSAL
   * swallows its own storage errors and resolves regardless, so the resolved promise says nothing
   * on its own about whether the cache is actually clean.
   */
  async clear(): Promise<void> {
    await this.msalService.instance.clearCache();
    if (this.hasAccount()) {
      throw new Error('MSAL cache still holds an account after clearCache()');
    }
  }
}
